import { useEffect, useState } from 'react';
import { ConfigProvider, Layout } from 'antd';
import 'antd/dist/reset.css';
import UnderConstruction from './UnderConstruction';
import HomePage from './pages/home/HomePage';
import HomeFooter from './pages/home/HomeFooter';
import HomeHeader from './pages/home/HomeHeader';
import DJDepotPage from './pages/djDepot/DJDepotPage';
import { fetchSession, signInWithGoogle, signOut } from './features/auth/authApi';
import { AuthRole, AuthUser } from './features/auth/types';
import './App.css';

const { Header, Content, Footer } = Layout;

type SiteTab = 'home' | 'dj-depot';
type AuthStatus = 'loading' | 'anonymous' | 'authenticated' | 'error';

const getTabFromQuery = (): SiteTab => {
  const params = new URLSearchParams(window.location.search);
  return params.get('tab') === 'dj-depot' ? 'dj-depot' : 'home';
};

const App = () => {
  const showMaintenance = process.env.REACT_APP_UNDER_CONSTRUCTION === 'true';
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID ?? '';
  const [activeTab, setActiveTab] = useState<SiteTab>(getTabFromQuery);
  const [authStatus, setAuthStatus] = useState<AuthStatus>('loading');
  const [authUser, setAuthUser] = useState<AuthUser | null>(null);
  const [authRole, setAuthRole] = useState<AuthRole>('dj');
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    const handlePopState = () => setActiveTab(getTabFromQuery());

    window.addEventListener('popstate', handlePopState);

    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    let active = true;

    const loadSession = async () => {
      try {
        const user = await fetchSession();

        if (!active) {
          return;
        }

        setAuthUser(user);
        setAuthStatus(user ? 'authenticated' : 'anonymous');
        setAuthRole(user?.role ?? 'dj');
      } catch (_error) {
        if (!active) {
          return;
        }

        setAuthStatus('error');
        setAuthError('Unable to load the current session.');
      }
    };

    loadSession();

    return () => {
      active = false;
    };
  }, []);

  const handleTabChange = (tab: SiteTab) => {
    setActiveTab(tab);

    const params = new URLSearchParams(window.location.search);
    params.set('tab', tab);

    const nextUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', nextUrl);

    if (tab === 'home') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleGoogleCredential = async (credential: string) => {
    setAuthError(null);
    setAuthStatus('loading');

    try {
      const user = await signInWithGoogle(credential, authRole);
      setAuthUser(user);
      setAuthStatus('authenticated');
      setAuthRole(user.role);
    } catch (error) {
      setAuthUser(null);
      setAuthStatus('error');
      setAuthError(
        error instanceof Error ? error.message : 'Google sign-in failed.',
      );
    }
  };

  const handleSignOut = async () => {
    setAuthError(null);

    try {
      await signOut();
      setAuthUser(null);
      setAuthStatus('anonymous');
    } catch (error) {
      setAuthStatus('error');
      setAuthError(
        error instanceof Error ? error.message : 'Unable to sign out.',
      );
    }
  };

  if (showMaintenance) {
    return <UnderConstruction />;
  }

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#1677ff',
          fontFamily:
            'Inter, "Segoe UI", system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
      }}
    >
      <Layout className="app-layout">
        <Header className="app-header">
          <HomeHeader activeTab={activeTab} onTabChange={handleTabChange} />
        </Header>

        <Content className="app-content">
          {activeTab === 'home' ? (
            <HomePage />
          ) : (
            <DJDepotPage
              authError={authError}
              authRole={authRole}
              authStatus={authStatus}
              authUser={authUser}
              googleClientId={googleClientId}
              onAuthRoleChange={setAuthRole}
              onGoogleCredential={handleGoogleCredential}
              onSignOut={handleSignOut}
            />
          )}
        </Content>

        <Footer className="app-footer">
          <HomeFooter />
        </Footer>
      </Layout>
    </ConfigProvider>
  );
};

export default App;
